import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FaWhatsapp } from 'react-icons/fa';
import { useLanguage } from '../../../contexts/LanguageContext';
import type { LocalizedText } from '../data/products';
import { getPrivateClassBySlug } from '../data/privateClasses';
import { buildWhatsAppLink } from '../utils/whatsapp';
import { isJakartaMonth } from '../utils/jakartaDate';
import { getProductBySlug } from '../utils/productBySlug';

const getText = (text: LocalizedText, locale: 'id' | 'en') =>
  locale === 'en' ? text.en : text.id;

const formatRupiah = (value: number) =>
  new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

type WorkflowStep = {
  title: string;
  desc: string;
};

type WorkflowStepperProps = {
  steps: WorkflowStep[];
};

const WorkflowStepper: React.FC<WorkflowStepperProps> = ({ steps }) => {
  return (
    <div className="relative">
      <div className="absolute left-4 top-0 h-full w-px bg-gray-200 md:hidden" />
      <div className="flex flex-col gap-6 md:flex-row md:items-stretch md:gap-4">
        {steps.map((step, index) => {
          const stepNumber = index + 1;
          return (
            <React.Fragment key={step.title}>
              <div className="relative pl-10 md:pl-0 md:flex-1">
                <div className="absolute left-0 top-0 flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-xs font-semibold text-dark-blue md:hidden">
                  {stepNumber}
                </div>
                <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm md:h-full">
                  <div className="mb-3 hidden h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-xs font-semibold text-dark-blue md:flex">
                    {stepNumber}
                  </div>
                  <p className="text-sm font-semibold text-dark-blue">{step.title}</p>
                  <p className="mt-2 text-sm text-gray-600">{step.desc}</p>
                </div>
                {index < steps.length - 1 && (
                  <span className="absolute left-4 top-8 h-6 w-px bg-gray-200 md:hidden" />
                )}
              </div>
              {index < steps.length - 1 && (
                <div className="relative hidden w-6 items-center justify-center md:flex">
                  <span className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-gray-200" />
                  <span className="text-xs font-semibold text-gray-400">→</span>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

const ProductDetailPage: React.FC = () => {
  const { language } = useLanguage();
  const { slug } = useParams();
  const privateClass = slug ? getPrivateClassBySlug(slug) : undefined;
  const product = slug ? getProductBySlug(slug) : undefined;
  const [selectedPackageId, setSelectedPackageId] = useState<string>('');

  const defaultPackage = useMemo(() => {
    if (!privateClass) {
      return undefined;
    }
    return (
      privateClass.pricing.packages.find((pkg) => pkg.default) ??
      privateClass.pricing.packages[0]
    );
  }, [privateClass]);

  useEffect(() => {
    if (privateClass) {
      document.title = `${privateClass.title} | Ourdo`;
    } else if (product) {
      document.title = `${getText(product.title, language)} | Ourdo`;
    }
  }, [product, privateClass, language]);

  useEffect(() => {
    if (defaultPackage) {
      setSelectedPackageId(defaultPackage.id);
    }
  }, [defaultPackage]);

  if (!product && !privateClass) {
    return (
      <div className="min-h-screen bg-light-blue text-dark-blue">
        <section className="mx-auto max-w-5xl px-4 py-16 text-center">
          <h1 className="text-3xl font-black md:text-4xl">
            {language === 'en' ? 'Product not found' : 'Produk tidak ditemukan'}
          </h1>
          <p className="mt-4 text-gray-600">
            {language === 'en'
              ? 'We could not find the product you are looking for.'
              : 'Kami tidak menemukan produk yang kamu cari.'}
          </p>
          <Link
            to="/"
            className="mt-8 inline-flex items-center justify-center rounded-xl border border-dark-blue/10 bg-white px-6 py-3 text-sm font-semibold text-dark-blue transition duration-200 hover:bg-dark-blue/5"
          >
            {language === 'en' ? 'Back to home' : 'Kembali ke beranda'}
          </Link>
        </section>
      </div>
    );
  }

  if (privateClass) {
    const { pricing } = privateClass;
    const selectedPackage =
      pricing.packages.find((pkg) => pkg.id === selectedPackageId) ?? defaultPackage;

    if (!selectedPackage) {
      return null;
    }

    const isPromoActive = pricing.promoMonthOnly === 1 && isJakartaMonth(1);
    const hasPromo =
      isPromoActive && pricing.basePriceValue > pricing.promoPriceValue;
    const discountPercent = hasPromo
      ? Math.round((1 - pricing.promoPriceValue / pricing.basePriceValue) * 100)
      : 0;

    const pricePerSession = hasPromo ? pricing.promoPriceValue : pricing.basePriceValue;
    const totalPrice = pricePerSession * selectedPackage.sessions;
    const totalBasePrice = pricing.basePriceValue * selectedPackage.sessions;

    const whatsappPackageMessage = `Halo admin Ourdo, saya mau ambil ${selectedPackage.label} untuk ${privateClass.title}.`;
    const whatsappLink = buildWhatsAppLink('6282151155507', whatsappPackageMessage);
    const preMeetingMessage = `Halo admin Ourdo, saya mau pre-meeting gratis 10–20 menit untuk ${privateClass.title}.`;
    const preMeetingLink = buildWhatsAppLink('6282151155507', preMeetingMessage);

    const flowSteps = [
      'Chat admin',
      'Pre-meeting',
      'Konfirmasi paket',
      'Booking',
      'Kelas jalan',
    ];

    return (
      <div className="min-h-screen bg-light-blue text-dark-blue">
        <section className="mx-auto max-w-5xl px-4 py-16">
          <div className="rounded-3xl border border-white/60 bg-white p-8 shadow-xl">
            <div className="mb-4 flex flex-wrap gap-2">
              <span className="rounded-full border border-dark-blue/10 bg-dark-blue/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-dark-blue">
                HOT
              </span>
              {isPromoActive && (
                <span className="rounded-full border border-black/10 bg-black/5 px-3 py-1 text-[11px] font-semibold text-dark-blue">
                  Hot Promo Januari
                </span>
              )}
              {hasPromo && (
                <span className="rounded-full border border-black/10 bg-black/5 px-3 py-1 text-[11px] font-medium text-dark-blue">
                  Diskon {discountPercent}%
                </span>
              )}
            </div>
            <h1 className="text-3xl font-black md:text-4xl">{privateClass.title}</h1>
            <p className="mt-3 text-base text-gray-600 md:text-lg">{privateClass.tagline}</p>
            <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold text-gray-600">
              <span className="rounded-full border border-gray-200 bg-white px-3 py-1">
                Online
              </span>
              <span className="rounded-full border border-gray-200 bg-white px-3 py-1">
                60-90 menit / sesi
              </span>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-xl bg-dark-blue px-6 py-3 text-sm font-semibold text-white shadow-lg transition duration-200 hover:bg-dark-accent"
              >
                Ambil Paket
              </a>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl border border-green-600/30 bg-green-50 px-6 py-3 text-sm font-semibold text-green-700 transition duration-200 hover:bg-green-100"
              >
                Chat WhatsApp
                <FaWhatsapp />
              </a>
              <a
                href={preMeetingLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-xl border border-dark-blue/10 bg-white px-6 py-3 text-sm font-semibold text-dark-blue transition duration-200 hover:bg-dark-blue/5"
              >
                Pre-meeting Gratis 10–20 menit
              </a>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-4 py-10">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="rounded-3xl bg-white p-6 shadow-lg">
              <h2 className="text-xl font-bold text-dark-blue">Yang dipelajari</h2>
              <ul className="mt-4 space-y-3 text-sm text-gray-600">
                {privateClass.learnList.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-1 h-2 w-2 rounded-full bg-accent" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl bg-white p-6 shadow-lg">
              <h2 className="text-xl font-bold text-dark-blue">Outcome</h2>
              <ul className="mt-4 space-y-3 text-sm text-gray-600">
                {privateClass.outcomeList.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-1 h-2 w-2 rounded-full bg-accent" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-4 py-10">
          <div className="rounded-3xl border border-black/5 bg-slate-50 p-6 shadow-lg md:p-8">
            <h2 className="text-2xl font-bold text-dark-blue">Pricing Paket</h2>
            <div className="mt-6 flex flex-wrap gap-3">
              {pricing.packages.map((pkg) => (
                <label
                  key={pkg.id}
                  className={`flex cursor-pointer items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold ${
                    selectedPackageId === pkg.id
                      ? 'border-dark-blue bg-white text-dark-blue'
                      : 'border-black/10 bg-white/70 text-gray-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="package"
                    className="hidden"
                    checked={selectedPackageId === pkg.id}
                    onChange={() => setSelectedPackageId(pkg.id)}
                  />
                  {pkg.label}
                </label>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap items-baseline gap-3">
              <span className="text-3xl font-black text-dark-blue">
                {formatRupiah(pricePerSession)}
                <span className="text-sm font-semibold text-gray-500"> / sesi</span>
              </span>
              {hasPromo && (
                <span className="text-sm text-gray-400 line-through">
                  {formatRupiah(pricing.basePriceValue)}
                </span>
              )}
            </div>
            <div className="mt-4 flex flex-wrap items-baseline gap-3">
              <span className="text-lg font-bold text-dark-blue">
                Total {formatRupiah(totalPrice)}
              </span>
              {hasPromo && (
                <span className="text-sm text-gray-400 line-through">
                  {formatRupiah(totalBasePrice)}
                </span>
              )}
            </div>
            <p className="mt-4 text-sm text-gray-600">{pricing.note}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-xl bg-dark-blue px-6 py-3 text-sm font-semibold text-white shadow-lg transition duration-200 hover:bg-dark-accent"
              >
                Ambil Paket
              </a>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl border border-green-600/30 bg-green-50 px-6 py-3 text-sm font-semibold text-green-700 transition duration-200 hover:bg-green-100"
              >
                Chat WhatsApp
                <FaWhatsapp />
              </a>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-4 py-10">
          <div className="rounded-3xl bg-white p-6 shadow-lg">
            <h2 className="text-lg font-bold text-dark-blue">Flow singkat</h2>
            <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold text-gray-600">
              {flowSteps.map((step) => (
                <span
                  key={step}
                  className="rounded-full border border-black/10 bg-white px-3 py-2"
                >
                  {step}
                </span>
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  const whatsappLink = buildWhatsAppLink(
    '6282151155507',
    getText(product.whatsappMessage, language)
  );

  const pricingSingleMessage: LocalizedText = {
    id: 'Halo admin Ourdo, saya mau Ourdo Buddy Session 1x (60 menit, Rp150.000). Topik saya: ____',
    en: 'Hi Ourdo admin, I want a single Ourdo Buddy Session (60 minutes, IDR 150k). My topic is: ____',
  };

  const pricingBundleMessage: LocalizedText = {
    id: 'Halo admin Ourdo, saya mau Bundle Ourdo Buddy Session 3x (Rp450.000). Topik saya: ____',
    en: 'Hi Ourdo admin, I want the Ourdo Buddy Session 3x bundle (IDR 450k). My topic is: ____',
  };

  const pricingSingleLink = buildWhatsAppLink(
    '6282151155507',
    getText(pricingSingleMessage, language)
  );
  const pricingBundleLink = buildWhatsAppLink(
    '6282151155507',
    getText(pricingBundleMessage, language)
  );

  return (
    <div className="min-h-screen bg-light-blue text-dark-blue">
      <section className="mx-auto max-w-5xl px-4 py-16">
        <div className="rounded-3xl border border-white/60 bg-white p-8 shadow-xl">
          {product.badges && product.badges.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2">
              {product.badges.map((badge) => (
                <span
                  key={badge}
                  className="rounded-full border border-dark-blue/10 bg-dark-blue/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-dark-blue"
                >
                  {badge}
                </span>
              ))}
            </div>
          )}
          <h1 className="text-3xl font-black md:text-4xl">
            {getText(product.title, language)}
          </h1>
          <p className="mt-3 text-base text-gray-600 md:text-lg">
            {getText(product.tagline, language)}
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-sm font-semibold text-gray-600">
            {[product.priceMainLabel, product.durationLabel, product.formatLabel]
              .filter(Boolean)
              .map((item) => (
                <span
                  key={(item as LocalizedText).id}
                  className="rounded-full border border-gray-200 bg-white px-4 py-2"
                >
                  {getText(item as LocalizedText, language)}
                </span>
              ))}
          </div>
          <div className="mt-8">
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition duration-200 hover:bg-green-700"
            >
              {language === 'en' ? 'Chat on WhatsApp' : 'Chat WhatsApp'}
              <FaWhatsapp />
            </a>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-10">
        <div className="rounded-3xl bg-white p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-dark-blue">
            {language === 'en' ? 'Pricing' : 'Harga'}
          </h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-gray-200 bg-white p-6">
              <p className="text-sm font-semibold text-gray-500">
                {language === 'en' ? 'Single session' : 'Sesi 1x'}
              </p>
              <p className="mt-3 text-sm text-gray-400 line-through">
                {language === 'en' ? 'IDR 350k' : 'Rp350.000'}
              </p>
              <p className="text-3xl font-black text-dark-blue">
                {language === 'en' ? 'IDR 150k' : 'Rp150.000'}
              </p>
              <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-emerald-600">
                {language === 'en' ? 'Launch promo' : 'Promo launching'}
              </p>
              <a
                href={pricingSingleLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2 text-xs font-semibold text-white transition duration-200 hover:bg-green-700"
              >
                {language === 'en' ? 'Chat on WhatsApp' : 'Chat WhatsApp'}
                <FaWhatsapp />
              </a>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-white p-6">
              <p className="text-sm font-semibold text-gray-500">
                {language === 'en' ? 'Bundle 3 sessions' : 'Bundle 3 sesi'}
              </p>
              <p className="mt-3 text-3xl font-black text-dark-blue">
                {language === 'en' ? 'IDR 450k' : 'Rp450.000'}
              </p>
              <p className="mt-2 text-sm text-gray-600">
                {language === 'en'
                  ? 'Best for iterative progress (review → improvements → final check).'
                  : 'Cocok untuk progres bertahap (review → perbaikan → final check).'}
              </p>
              <a
                href={pricingBundleLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 rounded-xl border border-green-600/30 bg-green-50 px-4 py-2 text-xs font-semibold text-green-700 transition duration-200 hover:bg-green-100"
              >
                {language === 'en' ? 'Chat on WhatsApp' : 'Chat WhatsApp'}
                <FaWhatsapp />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-10">
        <div className="rounded-3xl bg-white p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-dark-blue">
            {language === 'en' ? 'What you get' : 'Yang kamu dapat'}
          </h2>
          <ul className="mt-6 space-y-3 text-sm text-gray-600">
            {product.highlights.map((item) => (
              <li key={item.id} className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 rounded-full bg-accent" />
                <span>{getText(item, language)}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-10">
        <div className="rounded-3xl border border-gray-200 bg-slate-50 p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-dark-blue">
            {language === 'en' ? 'Workflow' : 'Workflow'}
          </h2>
          <div className="mt-6">
            <WorkflowStepper
              steps={[
                {
                  title: language === 'en' ? 'WhatsApp Chat (Intake)' : 'Chat WA (Intake)',
                  desc:
                    language === 'en'
                      ? 'Share topic + context, lock scope.'
                      : 'Jelaskan topik + konteks singkat. Admin bantu kunci scope.',
                },
                {
                  title: language === 'en' ? 'Pre-meeting (optional)' : 'Pre-meeting (opsional)',
                  desc:
                    language === 'en'
                      ? '10–20 mins alignment.'
                      : '10–20 menit untuk memastikan tujuan & bahan siap.',
                },
                {
                  title: language === 'en' ? 'Confirm package & rules' : 'Konfirmasi paket & aturan',
                  desc:
                    language === 'en'
                      ? 'Price, policy, no sensitive data.'
                      : 'Harga, durasi, aturan reschedule/cancel, dan batasan data sensitif.',
                },
                {
                  title: language === 'en' ? 'Schedule booking' : 'Booking jadwal',
                  desc:
                    language === 'en'
                      ? 'Pick session time.'
                      : 'Tentukan waktu sesi 60 menit.',
                },
                {
                  title:
                    language === 'en'
                      ? '60-min session + action list'
                      : 'Sesi 60 menit + action list',
                  desc:
                    language === 'en'
                      ? 'Recap, deep work, next steps.'
                      : 'Recap → deep work → rangkuman next steps.',
                },
              ]}
            />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-10">
        <div className="rounded-3xl bg-white p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-dark-blue">FAQ</h2>
          <div className="mt-6 space-y-4">
            {product.faq.map((item) => (
              <details
                key={item.q.id}
                className="rounded-2xl border border-gray-200 bg-white px-5 py-4"
              >
                <summary className="cursor-pointer text-sm font-semibold text-dark-blue">
                  {getText(item.q, language)}
                </summary>
                <p className="mt-3 text-sm text-gray-600">
                  {getText(item.a, language)}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-10">
        <div className="rounded-3xl bg-white p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-dark-blue">
            {language === 'en' ? 'Rules' : 'Aturan'}
          </h2>
          <ul className="mt-6 space-y-3 text-sm text-gray-600">
            {product.rules.map((item) => (
              <li key={item.id} className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 rounded-full bg-accent" />
                <span>{getText(item, language)}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-10">
        <div className="text-center text-xs text-gray-500">
          {language === 'en'
            ? 'Need help choosing the right session? Chat us anytime.'
            : 'Butuh bantuan memilih sesi yang tepat? Chat kami kapan saja.'}
        </div>
      </section>
    </div>
  );
};

export default ProductDetailPage;
